import React, { useEffect, useRef } from "react";
import mark from "../Assets/AWLeaf.png";

const SPACING = 7; // px between dot centres
const LEAF = "143, 209, 106";

// Renders the AW mark as a halftone: the logo is sampled on a grid and each
// dot is sized by how much of its cell the mark covers. A nod to the image
// processing work. Draws once, revealing left to right unless motion is reduced.
function Halftone({ className = "" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let raf = 0;
    let cancelled = false;
    let started = false;
    // Hidden below lg (display: none). Draw the first time it gets a size, e.g. after a resize.
    const resize = new ResizeObserver(() => {
      if (!started && canvas.offsetWidth > 0) {
        started = true;
        resize.disconnect();
        start();
      }
    });
    resize.observe(canvas);

    const start = () => {
      const img = new Image();
      img.src = mark;
      img.onload = () => {
        if (cancelled) return;
        const dpr = window.devicePixelRatio || 1;
        const { width, height } = canvas.getBoundingClientRect();
        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);
        const ctx = canvas.getContext("2d");
        ctx.scale(dpr, dpr);

        // Fit the mark into the canvas, then measure coverage per grid cell.
        const cols = Math.floor(width / SPACING);
        const rows = Math.floor(height / SPACING);
        const scale = Math.min(cols / img.width, rows / img.height);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const ox = Math.floor((cols - w) / 2);
        const oy = Math.floor((rows - h) / 2);
        const sample = document.createElement("canvas");
        sample.width = cols;
        sample.height = rows;
        const sctx = sample.getContext("2d");
        sctx.drawImage(img, ox, oy, w, h);
        const alpha = sctx.getImageData(0, 0, cols, rows).data;

        const draw = (upToCol) => {
          ctx.clearRect(0, 0, width, height);
          for (let y = 0; y < rows; y++) {
            for (let x = 0; x < Math.min(cols, upToCol); x++) {
              const a = alpha[(y * cols + x) * 4 + 3] / 255;
              const cx = x * SPACING + SPACING / 2;
              const cy = y * SPACING + SPACING / 2;
              ctx.beginPath();
              if (a > 0.04) {
                ctx.fillStyle = `rgba(${LEAF}, ${0.55 + 0.4 * a})`;
                ctx.arc(cx, cy, (SPACING / 2) * (0.35 + 0.6 * a), 0, Math.PI * 2);
              } else {
                ctx.fillStyle = `rgba(${LEAF}, 0.035)`;
                ctx.arc(cx, cy, 0.9, 0, Math.PI * 2);
              }
              ctx.fill();
            }
          }
        };

        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduce) {
          draw(cols);
          return;
        }
        const t0 = performance.now();
        const duration = 1100;
        const step = (now) => {
          const t = Math.min(1, (now - t0) / duration);
          const eased = 1 - Math.pow(1 - t, 3);
          draw(Math.ceil(eased * cols));
          if (t < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
      };
    };
    return () => {
      cancelled = true;
      resize.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
}

export default Halftone;
